<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "@/assets/api/openapi"

const operationStore = useOperationStore()
const { startLoading, stopLoading } = useLoading()
const { getWorkPermitDataAh } = useAppApi()

const schema = yup.object().shape({
  result: yup
    .string()
    .required("Pflichtfeld")
    .test(
      "is-not-open",
      "Der Arbeitsschritt kann nicht abgeschlossen werden, solange das Ergebnis nicht vorliegt",
      yupTestOnlyOnCreate((value) => value !== "open")
    )
    .test(
      "is-professional-license-status-not-present",
      "Der Arbeitsschritt kann nicht abgeschlossen werden, solange der Stand der Berufserlaubnis nicht vorliegt",
      yupTestOnlyOnCreate((value) =>
        Boolean(value !== "granted" || workPermitData.value?.status)
      )
    ),
  dateDetermination: yup.string().when("result", {
    is: (v: string) => v && v === "notRequired",
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),
})

const workPermitData = ref<components["schemas"]["BerufserlaubnisReview"]>()

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () =>
    (operationStore.currentWorkStep?.data ?? {
      result: undefined,
    }) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

const resultOptions: IffRadioGroupOption[] = [
  {
    label: "Offen",
    value: "open",
  },
  {
    label: "Berufserlaubnis abgeschlossen",
    value: "granted",
  },
  {
    label: "Berufserlaubnis nicht beantragt",
    value: "notRequired",
  },
]

const fetchData = async () => {
  if (operationStore.currentWorkStepId) {
    try {
      startLoading()

      const response = await getWorkPermitDataAh(
        operationStore.currentWorkStepId
      )

      if (response.status === 200 && response.data) {
        workPermitData.value = response.data
      }
    } catch {
      showError({
        statusCode: 500,
      })
    } finally {
      stopLoading()
    }
  }
}

const statusTextMap: Record<string, string> = {
  granted: "Erteilt am <DATE>",
  extended: "Verlängert am <DATE>",
  rewritten: "Umgeschrieben am <DATE>",
  revoked: "Widerrufen am <DATE>",
}

const statusText = computed(() => {
  const value = workPermitData.value?.status
  if (!value) return "-"
  return statusTextMap[value].replace(
    "<DATE>",
    workPermitData.value?.date ?? "?"
  )
})

const statusProfessionalLicenceDataFields = computed<DataField[]>(() => {
  return [
    {
      label: "Berufserlaubnis",
      value: workPermitData?.value?.date ? statusText.value : "-",
    },
    {
      label: "Gültigkeit",
      value:
        workPermitData?.value?.validFrom && workPermitData?.value?.validTo
          ? `${workPermitData?.value?.validFrom} - ${workPermitData?.value?.validTo}`
          : "-",
    },
    {
      label: "Auflage 1 (Träger)",
      value: workPermitData?.value?.condition1 ?? "-",
    },
    {
      label: "Auflage 2",
      value: workPermitData?.value?.condition2 ?? "-",
    },
  ]
})

watch(
  () => state.result,
  (value) => {
    if (value !== "notRequired") {
      state.dateDetermination = undefined
    }
  }
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Organisation Berufserlaubnis"
        @call-update="fetchData"
      />
    </template>

    <IffContentSection headline="Stand der Berufserlaubnis">
      <IffDataFields :data="statusProfessionalLicenceDataFields" />
    </IffContentSection>

    <IffContentSection
      headline="Abschließendes Ergebnis der Berufserlaubnis"
      :is-last-child="true"
    >
      <IffRadioGroup
        legend="Ergebnis"
        name="result"
        v-model="state.result"
        :options="resultOptions"
        required
        aria-controls="determination-date-region"
      />
      <div id="determination-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="state.result === 'notRequired'"
            name="dateDetermination"
            label="Datum der Feststellung"
            required
            class="max-w-[200px]"
          >
            <template #hint>
              <UButton
                @click="
                  state.dateDetermination = new Date()
                    .toISOString()
                    .slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>
            <UInput
              v-model="state.dateDetermination"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
